import Drawer from "@mui/material/Drawer";
import withStyles from "@mui/styles/withStyles";
import { activateNewCapacitySettingsDesign } from "@recare/core/model/utils/featureFlags";
import { SidebarMenuItem, TourElement } from "@recare/core/types";
import { NewBadge } from "ds/components/Badge";
import { Circle } from "ds/components/Circles";
import {
  ACCENT_COLOR,
  PROVIDER_PRIMARY_DARK_COLOR,
  PROVIDER_PRIMARY_LIGHT_COLOR,
  TEXT_DARK_PRIMARY,
  WHITE,
} from "ds/materials/colors";
import { Divider } from "ds/materials/layouts";
import {
  APP_BAR_HEIGHT,
  Z_INDEX_APP_BAR,
  dp,
  margin,
  sizing,
} from "ds/materials/metrics";
import { FONT_WEIGHT_BOLD, Subheading } from "ds/materials/typography";
import styled from "styled-components";

const StyledDrawer = withStyles({
  root: {
    zIndex: 1,
  },
})(Drawer);

const StyledSidebarDrawer = styled(Drawer)`
  .MuiPaper-root {
    z-index: ${Z_INDEX_APP_BAR - 1};
  }
`;

export const CAREPROVIDER_SETTINGS_SIDEBAR_WIDTH = sizing(31);

const Sidebar = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${WHITE};
  width: ${CAREPROVIDER_SETTINGS_SIDEBAR_WIDTH};
  height: calc(100% - ${dp(APP_BAR_HEIGHT)});
`;

const MenuItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${margin(11, 0, 0, 0)};
`;

const MenuItemContainer = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${sizing(5.5)};
  width: ${sizing(27)};
  border-radius: ${sizing(0.5)};
  background-color: ${(props) =>
    props.active ? PROVIDER_PRIMARY_LIGHT_COLOR : "transparent"};
  cursor: pointer;
  &:hover > h2 {
    font-weight: ${FONT_WEIGHT_BOLD};
  }
`;

const IconWrap = styled.div`
  margin: ${margin(0, 0, 0, 2)};
`;

function MenuItem({
  active,
  divider,
  icon,
  label,
  onClick,
  showBadge,
  showNotification,
  tour,
}: {
  active: boolean;
  divider?: boolean;
  icon: any;
  label: string;
  onClick: () => void;
  showBadge?: boolean;
  showNotification?: boolean;
  tour?: TourElement;
}) {
  return (
    <>
      <MenuItemContainer onClick={onClick} active={active} {...tour}>
        <IconWrap>{icon}</IconWrap>
        <Subheading
          color={active ? PROVIDER_PRIMARY_DARK_COLOR : TEXT_DARK_PRIMARY}
          bold={active}
        >
          {label}
        </Subheading>
        {showNotification ? <Circle color={ACCENT_COLOR} /> : null}
        {showBadge ? <NewBadge /> : null}
      </MenuItemContainer>
      {divider && <Divider width={sizing(25)} margin={margin(2, 2, 2, 4)} />}
    </>
  );
}

export default function SidebarMenu({
  activeMenuItem,
  menuItems,
}: {
  activeMenuItem: number | undefined;
  menuItems: Array<SidebarMenuItem & { tour?: TourElement }>;
}) {
  const DrawerComponent = activateNewCapacitySettingsDesign
    ? StyledSidebarDrawer
    : StyledDrawer;

  return (
    <DrawerComponent variant="persistent" anchor="left" open>
      <Sidebar>
        <MenuItemsContainer>
          {menuItems.map(
            (
              {
                action,
                divider,
                icon,
                key,
                label,
                showBadge,
                showNotification,
                tour,
              },
              i,
            ) => (
              <MenuItem
                key={key}
                label={label}
                icon={icon}
                onClick={action}
                divider={divider}
                active={activeMenuItem === i}
                showBadge={showBadge}
                showNotification={showNotification}
                tour={tour}
              />
            ),
          )}
        </MenuItemsContainer>
      </Sidebar>
    </DrawerComponent>
  );
}
